import { useState, useCallback } from 'react';
import { Mail, Phone, Copy, Check, ChevronDown, ChevronUp, HelpCircle, MessageSquare, Search } from 'lucide-react';
import Layout from '../../components/Layout';

type FaqItem = {
  question: string;
  answer: string;
};

type FaqCategory = 'petOwner' | 'veterinarian' | 'serviceProvider';
type FaqData = Record<FaqCategory, FaqItem[]>;

const FAQItem: React.FC<{ faq: FaqItem; index: number; expandedFaq: number | null; onToggle: (index: number) => void }> = ({
  faq,
  index,
  expandedFaq,
  onToggle,
}) => {
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-200">
      <button
        onClick={() => onToggle(index)}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors text-left bg-white"
      >
        <span className="font-semibold text-gray-800 flex-1 text-left pr-4">{faq.question}</span>
        <div className="flex-shrink-0 transition-transform duration-200" style={{ transform: expandedFaq === index ? 'rotate(180deg)' : 'rotate(0deg)' }}>
          <ChevronDown className="w-5 h-5 text-purple-600" />
        </div>
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${expandedFaq === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
          }`}
      >
        <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-purple-50 border-t border-gray-200">
          <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
        </div>
      </div>
    </div>
  );
};

const ContactCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  description: string;
  contactInfo: string;
  onClick: () => void;
  buttonText: React.ReactNode;
  gradientFrom: string;
  gradientTo: string;
}> = ({
  icon,
  title,
  description,
  contactInfo,
  onClick,
  buttonText,
  gradientFrom,
  gradientTo,
}) => (
    <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5 text-center hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5 group">
      <div
        className={`w-12 h-12 bg-gradient-to-br from-${gradientFrom} via-${gradientTo} to-${gradientTo} rounded-full flex items-center justify-center mx-auto mb-3 shadow-md group-hover:scale-105 transition-transform duration-300`}
      >
        {icon}
      </div>

      <h3 className="text-lg font-semibold text-gray-900 mb-1">{title}</h3>
      <p className="text-gray-600 text-sm mb-3 leading-snug">{description}</p>

      <p className="text-base font-semibold text-gray-800 mb-4 bg-gray-50 px-3 py-1 rounded-md inline-block">
        {contactInfo}
      </p>

      {/* <button
        onClick={onClick}
        className={`px-4 py-2 bg-gradient-to-r from-${gradientFrom} to-${gradientTo} text-white rounded-md font-medium hover:from-${gradientFrom.replace(
          '600',
          '700'
        )} hover:to-${gradientTo.replace(
          '400',
          '500'
        )} transition-all duration-200 flex items-center gap-2 mx-auto text-sm shadow-sm hover:shadow-md`}
      >
        {buttonText}
      </button> */}
    </div>
  );


export default function SupportDashboard() {
  const supportEmail = 'admin-support@example.com';
  const supportPhone = '+1 (800) 123-4567';
  const [copied, setCopied] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const [subTab, setSubTab] = useState<FaqCategory>('petOwner');
  const [searchQuery, setSearchQuery] = useState(''); // Added search for FAQs

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(supportEmail);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [supportEmail]);

  const handleEmailClick = useCallback(() => {
    window.location.href = `mailto:${supportEmail}`;
  }, [supportEmail]);

  const handlePhoneClick = useCallback(() => {
    window.location.href = `tel:${supportPhone}`;
  }, [supportPhone]);

  const handleFaqClick = useCallback((index: number) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  }, [expandedFaq]);

  const faqData: FaqData = {
    petOwner: [
      { question: "Is your platform cloud-based?", answer: "Yes, you can access the platform anytime from your device with internet." },
      { question: "How is my data and teleconsultation secured?", answer: "We use secure encryption and protocols to protect your privacy." },
      { question: "How easy is it to start using the platform?", answer: "Just register, add your pet(s), and start booking appointments or shopping." },
      { question: "Can I add multiple pets on the app?", answer: "Yes, manage all your pets under one account." },
      { question: "Can I book appointments online?", answer: "Yes, teleconsultations and on-site bookings are simple via the app." },
      { question: "How do I keep track of my pet’s health records?", answer: "All medical history and prescriptions are saved securely in the app." },
      { question: "How do I get reminders for vaccinations or medications?", answer: "Automated notifications keep you updated on your pet’s care schedule." },
      { question: "Can I get medicines or pet food delivered after consultation?", answer: "Yes, order products directly through the app." },
      { question: "What if my pet needs emergency care?", answer: "Use the emergency contact feature or call support immediately." },
      { question: "How do I update payment or delivery details?", answer: "Update your information anytime in “Account Settings.”" },
    ],
    veterinarian: [
      { question: "Is your platform cloud-based?", answer: "Yes, it’s accessible anytime from any device with internet." },
      { question: "How do I set up my profile?", answer: "Complete your professional and availability details in the Profile section." },
      { question: "How do I join a teleconsultation?", answer: "Click “Join” on your appointment at the scheduled time." },
      { question: "What if the video or audio isn’t working?", answer: "Check device permissions, restart app/device, or contact support." },
      { question: "How do I access past patient records?", answer: "Patient profiles include a “Medical History” section with records." },
      { question: "How is patient data privacy ensured?", answer: "Data is encrypted and accessible only to authorized users." },
      { question: "How do I view my earnings?", answer: "Check the “Payments” or “Earnings” section in your profile." },
      { question: "When will I receive payments?", answer: "Payments are processed monthly via bank transfer." },
      { question: "How do I report bugs or request features?", answer: "Use “New Ticket” with appropriate categories." },
      { question: "How do I reset my password?", answer: "Use “Forgot Password” on the login screen." },
    ],
    serviceProvider: [
      { question: "Is your platform cloud-based?", answer: "Yes, accessible anytime online from your device." },
      { question: "How do I create or update my service listing?", answer: "Go to “My Services” to add or edit listings." },
      { question: "How do I confirm or cancel a Appointment?", answer: "Manage bookings in the “Appointments” section." },
      { question: "How do I handle refunds or disputes?", answer: "Submit a ticket under “Payment Issues” with details." },
      { question: "How and when do I receive payments?", answer: "Payments are processed on a regular cycle; see “Payments” for details." },
      { question: "What do I do if my listing is not visible?", answer: "Ensure your listing is active and complete; contact support if needed." },
      { question: "How do I report a problem or request new features?", answer: "Use “New Ticket” and select the relevant category or “Other.”" },
      { question: "How do I reset my password?", answer: "Use the “Forgot Password” option at login." },
    ],
  };

  const currentFaqs = faqData[subTab].filter(faq =>
    faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-purple-50 p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto space-y-8">
          <div className="flex flex-col items-start justify-center text-left  sm:p-10 max-w-2xl">
            <div className="flex items-center justify-center mb-6">
              <div className="w-20 h-20 bg-purple-100 text-purple-700 rounded-2xl flex items-center justify-center shadow-lg">
                <MessageSquare className="w-10 h-10" />
              </div>
            </div>

            <h1 className="text-4xl font-extrabold text-gray-900  tracking-tight">
              Admin Support Panel
            </h1>

            <p className="text-gray-600 text-lg leading-relaxed">
              Use this panel to manage support tickets, contact the internal support team,
              or escalate issues with ease.
            </p>

            <div className=" w-32 h-1 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full" />
          </div>

          {/* <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg p-4 sm:p-6 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <input
              type="text"
              value={supportEmail}
              readOnly
              className="flex-1 px-4 py-3 bg-gray-50/50 rounded-xl border border-gray-200 text-gray-700 font-medium focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
              placeholder="Support email"
            />
            <button
              onClick={handleCopy}
              className="p-3 rounded-xl hover:bg-gray-100 transition-all duration-200 flex-shrink-0"
              title={copied ? 'Copied!' : 'Copy Email'}
              aria-label={copied ? 'Copied!' : 'Copy Email'}
            >
              {copied ? (
                <Check className="w-5 h-5 text-green-600 animate-pulse" />
              ) : (
                <Copy className="w-5 h-5 text-gray-600" />
              )}
            </button>
            <button
              onClick={handleEmailClick}
              className="px-6 py-3 bg-gradient-to-r from-purple-600 to-purple-500 text-white rounded-xl font-medium hover:from-purple-700 hover:to-purple-600 transition-all duration-200 shadow-md hover:shadow-lg flex-shrink-0 whitespace-nowrap"
            >
              Contact Support
            </button>
          </div> */}

          <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl overflow-hidden">
            <div className="flex flex-col sm:flex-row border-b border-gray-200">
              <button
                onClick={() => setTabIndex(0)}
                className={`flex-1 px-6 py-4 font-medium transition-all duration-200 flex items-center justify-center gap-2 ${tabIndex === 0
                  ? 'text-purple-600 border-b-2 border-purple-600 bg-purple-50/80 shadow-inner'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-purple-600'
                  }`}
              >
                <MessageSquare className="w-5 h-5" />
                Contact Support
              </button>
              <button
                onClick={() => setTabIndex(1)}
                className={`flex-1 px-6 py-4 font-medium transition-all duration-200 flex items-center justify-center gap-2 ${tabIndex === 1
                  ? 'text-purple-600 border-b-2 border-purple-600 bg-purple-50/80 shadow-inner'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-purple-600'
                  }`}
              >
                <HelpCircle className="w-5 h-5" />
                FAQs
              </button>
            </div>

            <div className="p-4 sm:p-6 lg:p-8">
              {tabIndex === 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <ContactCard
                    icon={<Mail className="w-8 h-8 text-white" />}
                    title="Contact Us"
                    description="Send us an email and we'll get back to you within 24 hours"
                    contactInfo={supportEmail}
                    onClick={handleEmailClick}
                    buttonText={
                      <>
                        <Mail className="w-4 h-4" />
                        Send Email
                      </>
                    }
                    gradientFrom="purple-600"
                    gradientTo="purple-500"
                  />
                  <ContactCard
                    icon={<Phone className="w-8 h-8 text-white" />}
                    title="Call Support"
                    description="Speak directly with our support team. Available 24/7"
                    contactInfo={supportPhone}
                    onClick={handlePhoneClick}
                    buttonText={
                      <>
                        <Phone className="w-4 h-4" />
                        Call Now
                      </>
                    }
                    gradientFrom="teal-600"
                    gradientTo="teal-400"
                  />
                </div>
              )}

              {tabIndex === 1 && (
                <div>
                  <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                    Frequently Asked Questions
                  </h3>

                  <div className="flex flex-wrap -mx-2 border-b border-gray-200 mb-6 overflow-x-auto pb-2">
                    {(['petOwner', 'veterinarian', 'serviceProvider'] as FaqCategory[]).map((category) => (
                      <button
                        key={category}
                        onClick={() => setSubTab(category)}
                        className={`flex-1 min-w-[200px] px-4 py-3 m-1 font-medium transition-all duration-200 rounded-xl ${subTab === category
                          ? 'text-purple-600 bg-purple-50 shadow-md border-2 border-purple-200'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-purple-600'
                          }`}
                      >
                        {category.charAt(0).toUpperCase() + category.slice(1).replace(/([A-Z])/g, ' $1').trim()}
                      </button>
                    ))}
                  </div>

                  <div className="relative mb-6">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search FAQs..."
                      className="w-full pl-10 pr-4 py-3 bg-gray-50/50 rounded-xl border border-gray-200 text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
                    />
                  </div>

                  {currentFaqs.length > 0 ? (
                    <div className="space-y-4">
                      {currentFaqs.map((faq, index) => (
                        <FAQItem
                          key={`${subTab}-${index}`}
                          faq={faq}
                          index={index}
                          expandedFaq={expandedFaq}
                          onToggle={handleFaqClick}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-gray-500">
                      <HelpCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No FAQs found matching your search.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}